/**
 * Coleta de compra no fornecedor pela Lalamove (#2307): regras de tela, longe
 * do React para poder ser testadas.
 *
 * `textoMotorista` espelha App\Services\LalamoveColetaService::textoMotorista:
 * a tela abre com o mesmo texto que o servidor montaria, e o operador pode
 * editar antes de pedir. Se um dos dois mudar, o outro muda junto.
 */

const limpar = (v) => String(v ?? '').replace(/\s+/g, ' ').trim();

/** Modelo de fabrica (LalamoveColetaService::MODELO_TEXTO_PADRAO). */
export const MODELO_PADRAO = 'PEDIDOS\n{pedidos}\nRetirar em: {fornecedor}\n{complemento}\n{instrucoes}';

/** Variaveis do modelo (LalamoveColetaService::VARIAVEIS_TEXTO). */
export const VARIAVEIS = {
    '{pedidos}': 'Um pedido por linha: número do pedido do fornecedor, RETIRA e a palavra-chave, quando houver',
    '{fornecedor}': 'Nome do fornecedor (endereço de coleta)',
    '{complemento}': 'Complemento e ponto de referência do endereço de coleta',
    '{instrucoes}': 'Instrução padrão cadastrada no endereço do fornecedor',
};

/** O que impede o modelo de ser salvo (mesma regra do servidor). */
export const problemaDoModelo = (modelo) => {
    const texto = String(modelo ?? '');
    if (!texto.includes('{pedidos}')) return 'O texto precisa ter {pedidos}: é onde entram os números dos pedidos.';

    const desconhecida = (texto.match(/\{[^{}\s]*\}/gu) || []).find((v) => !VARIAVEIS[v]);
    return desconhecida
        ? `Variável desconhecida: ${desconhecida}. Use só ${Object.keys(VARIAVEIS).join(', ')}.`
        : null;
};

/**
 * Texto do motorista montado pelo modelo da Configuracao. Linha que fica
 * vazia (variavel sem valor) sai do texto.
 */
export const textoMotorista = ({ modelo = null, ponto, compras = [], complemento = null, observacao = '' }) => {
    const pedidos = compras.map((c) => {
        const chave = limpar(c.palavra_chave);
        return `${limpar(c.numero_pedido)} RETIRA${chave ? ` (palavra-chave: ${chave})` : ''}`;
    });

    const valores = {
        '{pedidos}': pedidos.join('\n'),
        '{fornecedor}': limpar(ponto?.nome),
        '{complemento}': complemento || '',
        '{instrucoes}': String(ponto?.instrucoes ?? '').trim(),
    };

    const base = (String(modelo ?? '').trim() || MODELO_PADRAO)
        .replace(/\{pedidos\}|\{fornecedor\}|\{complemento\}|\{instrucoes\}/g, (v) => valores[v]);

    const linhas = base.split(/\r\n|\n|\r/).map((l) => l.replace(/\s+$/, '')).filter((l) => l.trim() !== '');
    if (limpar(observacao)) linhas.push(String(observacao).trim());

    return linhas.join('\n');
};

/**
 * Nome do remetente: o que o motorista ve no ponto de coleta. Espelha
 * LalamoveColetaService::nomeRemetente (limite de 60 caracteres da Lalamove).
 */
export const nomeRemetente = (ponto, compras = []) => {
    const numeros = compras.map((c) => limpar(c.numero_pedido)).filter(Boolean);
    const nome = limpar(ponto?.contato_nome) || limpar(ponto?.nome);

    return (numeros.length ? `${nome} - Ped. ${numeros.join(', ')}` : nome).slice(0, 60);
};

/** Limite da Lalamove para o texto do motorista (remarks), com folga. */
export const LIMITE_TEXTO = 1400;

/** O que ainda impede de cotar, em portugues de operador. */
export const pendenciasDaSelecao = (selecionadas = [], max = 15) => {
    if (selecionadas.length === 0) return ['Marque pelo menos um pedido para a corrida.'];
    if (selecionadas.length > max) return [`Uma corrida leva no máximo ${max} pedidos.`];

    const semNumero = selecionadas.filter((c) => !limpar(c.numero_pedido));
    if (semNumero.length > 0) {
        return [`Informe o número do pedido do fornecedor em ${semNumero.map((c) => c.rotulo).join(', ')}.`];
    }

    return [];
};

export const chaveCompra = (c) => `${c.tipo}:${c.id}`;

const brl = (v) => `R$ ${Number(v || 0).toFixed(2).replace('.', ',')}`;

/** Cor e texto do selo da corrida no card da compra. */
export const seloDaCorrida = (corrida) => {
    if (!corrida) return null;

    const cor = {
        PENDENTE: 'gray',
        ASSIGNING_DRIVER: 'yellow',
        ON_GOING: 'blue',
        PICKED_UP: 'purple',
        COMPLETED: 'green',
        CANCELED: 'red',
        EXPIRED: 'red',
        REJECTED: 'red',
    }[corrida.status] || 'gray';

    const encerradaSemColeta = ['CANCELED', 'EXPIRED', 'REJECTED'].includes(corrida.status);

    return {
        cor,
        texto: corrida.pendente_sem_confirmacao ? 'Lalamove sem confirmação' : `Lalamove · ${corrida.status_rotulo}`,
        custo: corrida.preco_total != null && !encerradaSemColeta ? brl(corrida.preco_total) : null,
        encerradaSemColeta,
    };
};
